import {
  BarsArrowDownIcon,
  BarsArrowUpIcon,
  CalendarDaysIcon,
  CalendarIcon,
  ChevronDoubleDownIcon,
  PlusCircleIcon,
  PrinterIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  addDays,
  endOfDay,
  format,
  formatDistanceToNow,
  isFuture,
  isPast,
  isSameDay,
  isSameMonth,
  startOfDay,
  subMinutes,
} from "date-fns";
import { motion } from "framer-motion";
import useIntersectionObserver from "@/hooks/useIntersectionObserver";
import {
  DateRangeModes,
  absoluteDistanceInDays,
  findNextTerm,
  findPreviousTerm,
  findTerm,
  getAppropriateDateRange,
  getDateRangeTitle,
  isSameDayOrAfter,
  isSameDayOrBefore,
} from "@/lib/dates";
import { eventEmitter } from "@/lib/events";
import { OrderSessionType } from "@/lib/types";
import useLocalStorageState from "@/lib/useLocalStorageState";
import {
  classNames,
  getLocalStorage,
  isAdmin,
  isAssistant,
  isStudent,
  isTeacher,
  searchOrders,
  sortByFn,
} from "@/lib/util";
import mixpanel from "mixpanel-browser";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import {
  OrderSessionsQuery,
  OrderSessionsQueryVariables,
  Recipe,
  SortOrder,
  useDeleteManyOrderSessionsMutation,
  useOrderSessionsQuery,
} from "@/queries/autogenerate/graphql";
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import DatePicker from "react-datepicker";
import styled from "styled-components";
import { InformationCircleComponent, RestoreLinkButton } from "./Commons";
import DialogComponent from "./Dialog";
import ErrorMessage from "./ErrorMessage";
import OrderSession from "./OrderSession";
import { CurrentUser, NewTeacherContext, OrderDialogContext } from "./Page";
import { TitleBar } from "./styles/Layout";
import Tooltip from "./styles/Tooltip";
import UndrawEmpty from "./svg/UndrawEmpty";
import UndrawNotFound from "./svg/UndrawNotFound";
import UndrawQuestions from "./svg/UndrawQuestions";
import UndrawTeaching from "./svg/UndrawTeaching";

const OrdersGrid = styled.div`
  display: grid;
  grid-gap: 20px;
  grid-template-columns: repeat(auto-fit, minmax(600px, 1fr));
  grid-auto-rows: 40px;
  grid-auto-flow: dense;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  @media print {
    display: block;
  }
`;

interface DeleteManyOrderSessionsProps {
  searchedSessions: {
    orders: any;
    __typename?: "OrderSession";
    id: string;
    date: any;
    updatedAt: any;
    servings?: number | null | undefined;
    recipe?:
      | ({
          __typename?: "Recipe";
        } & Pick<Recipe, "id" | "name">)
      | null
      | undefined;
  }[];
  search: string;
  removeOrderSessions: (ids: string[]) => void;
}

const DeleteManyOrderSessions: React.FC<DeleteManyOrderSessionsProps> = ({
  searchedSessions,
  search,
  removeOrderSessions,
}) => {
  const [{ fetching, error }, deleteManyOrderSessions] =
    useDeleteManyOrderSessionsMutation();

  if (error) {
    console.log(error);
    return <p>Error</p>;
  }

  return (
    <Tooltip content={`Delete All ${searchedSessions.length} Orders`}>
      <button
        className="print:hidden"
        onClick={async () => {
          removeOrderSessions(searchedSessions.map((s) => s.id));
          await deleteManyOrderSessions({
            ids: searchedSessions.map((os) => os.id),
          });
        }}
        disabled={fetching || !!search || !searchedSessions.length}
      >
        {fetching ? (
          <div className="spinner"></div>
        ) : (
          <TrashIcon className="icon-md icon-orange" />
        )}
      </button>
    </Tooltip>
  );
};

// Temporal Indicators
// Fancy way of saying time indicators, they indicate different time periods for orders
// The closer to now, the smaller the time jumps.
// 1 day, 2 days, 1 week, 2 weeks, 3 weeks, 1 month, 2 months, 3 months, 4 months, 6 months, 1 year, 2 years
// 1 day, 2 days, 7 days, 14 days, 21, days, 30 days, 60 days, 90 days, 120 days, 180, 365 days, 730 days
// Way to factor in width? How many would fit into a row?

// Scan through and fill the time periods
// Depends on sorting, optionally reverse array depending on sorting

// Run through and check for date ranges.
// If b passes but a already did, check which would cross the booundary first i.e. which is closer to now

const temporalBoundaries: {
  days: number;
  next?: number;
}[] = [
  { days: 0, next: 1 },
  { days: 1, next: 2 },
  { days: 2, next: 7 },
  { days: 7, next: 14 },
  { days: 14, next: 21 },
  { days: 21, next: 30 },
  { days: 30, next: 60 },
  { days: 60, next: 90 },
  { days: 90, next: 120 },
  { days: 120, next: 180 },
  { days: 180, next: 365 },
  { days: 365, next: 730 },
  { days: 730, next: undefined },
];

function insertTemporalIndicators(
  orderSessions: OrderSessionsQuery["orderSessions"]
) {
  const now = new Date();

  const boundaries: string[] = [];

  temporalBoundaries.forEach(({ days, next }) => {
    if (days == 0) {
      const orderSession = orderSessions
        .filter((os) => isSameDay(now, new Date(os.date)))
        .reduce<OrderSessionsQuery["orderSessions"][number]>(
          (prev, curr) =>
            prev
              ? absoluteDistanceInDays(now, new Date(prev.date)) <
                absoluteDistanceInDays(now, new Date(curr.date))
                ? prev
                : curr
              : curr,
          orderSessions[0]
        );

      if (orderSession) boundaries.push(orderSession.id);

      return;
    }

    // Future
    // is future && difference is least
    const futureSession = orderSessions
      .filter(
        (os) =>
          isFuture(new Date(os.date)) &&
          absoluteDistanceInDays(now, new Date(os.date)) >= days &&
          absoluteDistanceInDays(now, new Date(os.date)) < (next || Infinity)
      )
      .reduce<OrderSessionsQuery["orderSessions"][number]>(
        (prev, curr) =>
          prev
            ? absoluteDistanceInDays(now, new Date(prev.date)) <
              absoluteDistanceInDays(now, new Date(curr.date))
              ? prev
              : curr
            : curr,
        orderSessions[0]
      );

    if (futureSession) boundaries.push(futureSession.id);

    // Past
    const pastSession = orderSessions
      .filter(
        (os) =>
          isPast(new Date(os.date)) &&
          absoluteDistanceInDays(now, new Date(os.date)) >= days &&
          absoluteDistanceInDays(now, new Date(os.date)) < (next || Infinity)
      )
      .reduce<OrderSessionsQuery["orderSessions"][number]>(
        (prev, curr) =>
          prev
            ? absoluteDistanceInDays(now, new Date(prev.date)) <
              absoluteDistanceInDays(now, new Date(curr.date))
              ? prev
              : curr
            : curr,
        orderSessions[0]
      );

    if (pastSession) boundaries.push(pastSession.id);
  });

  return orderSessions.map((os) => ({
    ...os,
    boundary: boundaries.includes(os.id),
  }));
}

// Later: consider how many fit into a row and use that to decide...

const TAKE_AMOUNTS = 200;

interface Props {
  user: CurrentUser;
}

const Orders: FC<Props> = ({ user }) => {
  const searchParams = useSearchParams();

  const [fragmentID, setFragmentID] = useState<string | null>(null);

  useEffect(() => {
    const found = window.location.hash.match(/#\S+/);

    if (found && found[0] && !["#classes", "#orders"].includes(found[0])) {
      console.log("An id as been set", found[0]);

      setFragmentID(found[0].replace("#", ""));
    } else {
      console.log("No id found");
      setFragmentID(null);
    }
  }, [searchParams]);

  const [search, setSearch] = useState("");
  const [dateRangeMode, setDateRangeMode] =
    useLocalStorageState<DateRangeModes>("ordersDateRangeMode", "this-week");
  const [sort, setSort] = useLocalStorageState<"ascending" | "descending">(
    "ordersSort",
    "descending"
  );

  const [initialStartDate, initialEndDate] =
    getAppropriateDateRange(dateRangeMode);

  /**
   * Why is this necessary?
   *
   * When dateRangeMode is first initialised, it has its default value
   * Which is then taken to be the value by {start,end}Date when initialiasing
   *
   * This initial value is never updated because state is only initialised once.
   *
   * The setTimeout function is a fix for this problem
   */
  useEffect(() => {
    const mode = getLocalStorage("ordersDateRangeMode", "this-week");
    const [start, end] = getAppropriateDateRange(mode, {
      terms: user.school?.terms || [],
    });
    setSourceStartDate(start);
    setSourceEndDate(end);
    // setTimeout(() => {
    // }, 500);
  }, []);

  const [sourceStartDate, setSourceStartDate] = useLocalStorageState(
    "dateRangeStart",
    initialStartDate
  );
  const [sourceEndDate, setSourceEndDate] = useLocalStorageState(
    "dateRangeEnd",
    initialEndDate
  );

  const setStartDate = (newValue: Date) =>
    setSourceStartDate(newValue.toJSON());
  const setEndDate = (newValue: Date) => setSourceEndDate(newValue.toJSON());

  const startDate = new Date(sourceStartDate);
  const endDate = new Date(sourceEndDate);

  const [showDistance, setShowDistance] = useLocalStorageState(
    "orderSessionDistance",
    true
  );

  // state variable to trigger an update as needed
  const [updateCount, setUpdateCount] = useState(0);

  const [cursor, setCursor] = useState<string | null>(null);
  // const [stale, setStale] = useState(false);

  // when the data returned is an empty array, we've reached the end of the log
  // this variable is used to hide the infinite scroll loader
  const [limitReached, setLimitReached] = useState(false);

  const [classes, setClasses] = useState<OrderSessionsQuery["classes"]>([]);
  const [orderSessions, setOrderSessions] = useState<
    NonNullable<OrderSessionsQuery["orderSessions"]>
  >([]);

  const addOrderSessions = useMemo(
    () => (newOrderSessions: OrderSessionType[]) => {
      if (!orderSessions) return;

      const uniqueNewSessions = newOrderSessions.filter(
        (nos) => !orderSessions.find((os) => os.id === nos.id)
      );
      const resultSessions = [...orderSessions, ...uniqueNewSessions];

      const sortedSessions =
        sort === "ascending"
          ? sortByFn(resultSessions, (os) => +new Date(os.createdAt))
          : sortByFn(resultSessions, (os) => +new Date(os.createdAt)).reverse();

      setOrderSessions(sortedSessions);
    },
    [orderSessions.length]
  );

  const removeOrderSessions = useMemo(
    () => (ids: string[]) =>
      orderSessions && ids
        ? setOrderSessions(orderSessions.filter((os) => !ids.includes(os.id)))
        : null,
    [orderSessions?.length]
  );

  const removeOrders = useMemo(
    () => (ids: string[]) =>
      orderSessions
        ? setOrderSessions(
            orderSessions.map((os) => ({
              ...os,
              orders: os.orders.filter((o) => !ids.includes(o.id)),
            }))
          )
        : null,
    [orderSessions.length]
  );

  const updateStaticOrderSession = useMemo(
    () => (orderSession: OrderSessionType) => {
      if (!orderSessions) return;

      setOrderSessions(
        orderSessions.map((os) =>
          os.id !== orderSession.id ? os : orderSession
        )
      );

      setUpdateCount(updateCount + 1);
    },
    [!!orderSessions?.length, updateCount]
  );

  const updateStaticOrder = useMemo(
    () => (order: OrderSessionType["orders"][number]) => {
      if (!orderSessions) return;

      setOrderSessions(
        orderSessions.map((os) =>
          os.orders.some((o) => o.id === order.id)
            ? {
                ...os,
                orders: os.orders.map((o) => (o.id !== order.id ? o : order)),
              }
            : os
        )
      );

      setUpdateCount(updateCount + 1);
    },
    [!!orderSessions?.length, updateCount]
  );

  const variables: OrderSessionsQueryVariables = {
    schoolID: user.school?.id || "",
    where: {
      createdAt: {
        gte: subMinutes(new Date(), 5),
      },
      date: {
        gte: startDate ? startOfDay(startDate) : new Date(),
        lte: endDate ? endOfDay(endDate) : new Date(),
      },
      orders: isTeacher(user)
        ? isAssistant(user) || isAdmin(user)
          ? {
              some: {
                deletedAt: null,
                classe: {
                  is: {
                    schoolId: { equals: user.school?.id },
                  },
                },
              },
            }
          : {
              some: {
                deletedAt: null,
                classe: {
                  is: { users: { some: { id: { equals: user.id } } } },
                },
              },
            }
        : { some: { deletedAt: null, userId: { equals: user.id } } },
      // OR: [
      //   { recipe: { is: { name: { search } } } },
      //   {
      //     orders: {
      //       some: {
      //         OR: [
      //           { ingredient: { is: { OR: [{ name: { search } }] } } },
      //           { user: { is: { name: { search } } } },
      //           { classe: { is: { name: { search } } } },
      //         ],
      //       },
      //     },
      //   },
      // ],
    },
    cursor: cursor ? { id: cursor } : undefined,
    take: TAKE_AMOUNTS,
    orderBy: {
      createdAt: sort === "ascending" ? SortOrder.Asc : SortOrder.Desc,
    },
  };

  const [{ fetching, error, data }, reexecuteQuery] = useOrderSessionsQuery({
    requestPolicy: "network-only",
    variables: {
      schoolID: user.school?.id || "",
      where: {
        date: {
          gte: startDate ? startOfDay(startDate) : new Date(),
          lte: endDate ? endOfDay(endDate) : new Date(),
        },
        orders: {
          some: {
            deletedAt: null,
            ingredient: {
              is: {
                deletedAt: null,
              },
            },
          },
        },
        OR: isTeacher(user)
          ? isAssistant(user) || isAdmin(user)
            ? [
                {
                  classe: {
                    is: {
                      schoolId: { equals: user.school?.id },
                    },
                  },
                },
                {
                  orders: {
                    some: {
                      classe: {
                        is: {
                          schoolId: { equals: user.school?.id },
                        },
                      },
                    },
                  },
                },
              ]
            : [
                {
                  classe: {
                    is: { users: { some: { id: { equals: user.id } } } },
                  },
                },
                {
                  orders: {
                    some: {
                      classe: {
                        is: { users: { some: { id: { equals: user.id } } } },
                      },
                    },
                  },
                },
              ]
          : [
              { userId: { equals: user.id } },
              { orders: { some: { userId: { equals: user.id } } } },
            ],
        // OR: [
        //   { recipe: { is: { name: { search } } } },
        //   {
        //     orders: {
        //       some: {
        //         OR: [
        //           { ingredient: { is: { OR: [{ name: { search } }] } } },
        //           { user: { is: { name: { search } } } },
        //           { classe: { is: { name: { search } } } },
        //         ],
        //       },
        //     },
        //   },
        // ],
      },
      cursor: cursor ? { id: cursor } : undefined,
      take: TAKE_AMOUNTS,
      orderBy: {
        createdAt: sort === "ascending" ? SortOrder.Asc : SortOrder.Desc,
      },
    },
  });

  useEffect(() => {
    const callback = () => {
      const tempSort = sort;

      setSort(sort === "ascending" ? "descending" : "ascending");
      setTimeout(() => {
        setSort(tempSort);
      }, 250);
    };
    eventEmitter.on("made-order", callback);

    return () => {
      eventEmitter.off("made-order", callback);
    };
  }, []);

  useEffect(() => {
    console.log("date and/or sort changed");

    setCursor(null);
    setClasses([]);
    setOrderSessions([]);
  }, [sourceStartDate, sourceEndDate, sort]);

  // Use an effect to update the logs state whenever the data changes
  // This is necessary because the data is paginated and the cursor is used to
  // determine the next page of data
  useEffect(() => {
    console.log("state change", fetching, !!error, data?.orderSessions?.length);

    if (fetching) {
      // If the data is being fetched, don't update the logs state
      return;
    } else if (error) {
      // If there is an error, don't update the logs state
      console.error(error);
      return;
    } else if (data) {
      // If there are 3 or less order sessions, expand them by defaul
      if (data.orderSessions.length <= 3) {
        setExpandedOrders(new Set(data.orderSessions.map(({ id }) => id)));
      }

      // If there is no error, update the logs state
      setClasses(data.classes || []);

      const orderSessionsToAdd = data.orderSessions.filter(
        (os) => !orderSessions?.find((os2) => os.id === os2.id)
      );

      console.log("orderSessionsToAdd", orderSessionsToAdd.length);
      if (orderSessions) {
        setOrderSessions([...orderSessions, ...orderSessionsToAdd]);
      } else {
        setOrderSessions(orderSessionsToAdd);
      }

      // update the cursor state
      if (data?.orderSessions.length >= TAKE_AMOUNTS) {
        // setCursor(data.logs[data.logs.length - 1].id);
        setLimitReached(false);
      } else {
        setLimitReached(true);
      }
    }
  }, [fetching, sourceStartDate, sourceEndDate, sort]);

  const [showDialog, setShowDialog] = useState(false);
  const open = () => setShowDialog(true);
  const close = () => setShowDialog(false);

  const [expandedOrders, setExpandedOrders] = useState(new Set<string>());

  const [deleted, setDeleted] = useState<string[]>([]);

  const searchedSessions = useMemo(
    () =>
      orderSessions
        ? orderSessions
            .filter((os) => !deleted.includes(os.id))
            .filter(
              (os) =>
                isAssistant(user) ||
                isAdmin(user) ||
                isStudent(user) ||
                os.orders.some((o) => classes.some((c) => c.id == o.classe.id))
            )
            .map((os) => ({
              ...os,
              orders: searchOrders(search, os.orders),
            }))
            .filter((os) => os.orders.length)
            .filter(
              (os) =>
                isSameDayOrAfter(new Date(os.date), startDate) &&
                isSameDayOrBefore(new Date(os.date), endDate)
            )
        : [],
    [
      fetching,
      orderSessions?.length,
      search,
      sourceStartDate,
      sourceEndDate,
      updateCount,
      deleted,
    ]
  );

  const hasFragmentID = (
    orderSession: OrderSessionsQuery["orderSessions"][number]
  ) =>
    orderSession.id == fragmentID ||
    orderSession.orders.some((o) => o.id == fragmentID);

  return (
    <div id="orders" className="space-y-3">
      <TitleBar sticky>
        <div style={{ display: "flex", alignItems: "center" }}>
          {fragmentID ? (
            <>
              <h2>Specific Order ({fragmentID})</h2>
              <Link prefetch={false} href={"/"} className="btn inline-block">
                See All Orders
              </Link>
            </>
          ) : (
            <>
              <h1>
                Orders{" "}
                {searchedSessions.length ? (
                  <span>({searchedSessions.length})</span>
                ) : null}
              </h1>
              <Tooltip
                content={
                  isStudent(user)
                    ? "As a student, you can only see orders you have made."
                    : isAdmin(user) || isAssistant(user)
                    ? "As an admin or assistant, you can see all orders made in the school."
                    : "As a teacher you can see orders made in your classes."
                }
              >
                <span>
                  <InformationCircleComponent />
                </span>
              </Tooltip>
            </>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          {fetching ? <div className="spinner" /> : null}
          {fragmentID ? null : (
            <>
              <Tooltip
                content={
                  <div className="flex flex-col gap-2">
                    <span>Click to toggle date format</span>
                    <small>
                      Exact Date:{" "}
                      {format(addDays(new Date(), 7), "E do MMM (h:mm a)")}
                    </small>
                    <small>
                      Distance:{" "}
                      {formatDistanceToNow(addDays(new Date(), 7), {
                        addSuffix: true,
                      })}
                    </small>
                  </div>
                }
              >
                <button
                  className="btn relative bg-white hover:bg-gray-50 print:hidden"
                  onClick={() => setShowDistance(!showDistance)}
                >
                  <CalendarDaysIcon className="w-6" />
                  <span className="font-normal text-gray-600">
                    {showDistance ? "Distance" : "Exact Date"}
                  </span>

                  <span className="absolute -left-1 -top-1 flex h-3 w-3 print:hidden">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-green-500"></span>
                  </span>
                </button>
              </Tooltip>
              <Link
                prefetch={false}
                href="/order-history"
                className="print:hidden"
              >
                <Tooltip content="Recover Deleted Orders">
                  <button className="">
                    <RestoreLinkButton />
                  </button>
                </Tooltip>
              </Link>
              <Tooltip
                content={`Sort - ${
                  sort == "ascending" ? "Oldest to Newest" : "Newest to Oldest"
                }`}
              >
                <button
                  className="print:hidden"
                  onClick={() => {
                    if (sort == "ascending") {
                      setSort("descending");
                    } else {
                      setSort("ascending");
                    }
                  }}
                >
                  {sort == "ascending" ? (
                    <BarsArrowUpIcon className="icon-md icon-gray" />
                  ) : (
                    <BarsArrowDownIcon className="icon-md icon-gray" />
                  )}
                </button>
              </Tooltip>
              <div className="mx-1.5 flex items-center space-x-2.5 rounded-md border border-solid border-gray-300 bg-white px-1.5 shadow-sm">
                <div
                  className="group flex cursor-pointer items-center gap-2 text-gray-500 hover:text-gray-600"
                  onClick={open}
                >
                  <CalendarIcon className="w-5 text-gray-600 group-hover:text-gray-700" />
                  <span>
                    {format(startDate || new Date(), "MMM do")} &harr;{" "}
                    {isSameMonth(startDate || new Date(), endDate || new Date())
                      ? format(endDate || new Date(), "do")
                      : format(endDate || new Date(), "MMM do")}
                  </span>
                  <DialogComponent
                    isOpen={showDialog}
                    onClose={close}
                    aria-label="Confirm action"
                    className="relative"
                  >
                    <div
                      className="grid"
                      style={{ gridTemplateColumns: "200px 1fr" }}
                    >
                      <div className="flex flex-col justify-around gap-1.5 p-2">
                        {(
                          [
                            "today",
                            "last-week",
                            "this-week",
                            "next-week",
                            "last-term",
                            "this-term",
                            "next-term",
                            "this-year",
                            "past-present",
                            "present-future",
                            "all-time",
                          ] as DateRangeModes[]
                        )
                          .filter((mode) =>
                            mode == "this-term"
                              ? findTerm(new Date(), user.school?.terms || [])
                              : mode == "last-term"
                              ? findPreviousTerm(
                                  new Date(),
                                  user.school?.terms || []
                                )
                              : mode == "next-term"
                              ? findNextTerm(
                                  new Date(),
                                  user.school?.terms || []
                                )
                              : true
                          )
                          .map((mode) => (
                            <button
                              key={mode}
                              className={`cursor-pointer rounded border-none p-2 ${
                                dateRangeMode == mode
                                  ? "bg-gray-300 hover:bg-gray-300"
                                  : "bg-gray-100 hover:bg-gray-200"
                              }`}
                              onClick={() => {
                                const [start, end] = getAppropriateDateRange(
                                  mode,
                                  {
                                    terms: user.school?.terms || [],
                                  }
                                );

                                setSourceStartDate(start);
                                setSourceEndDate(end);
                                setDateRangeMode(mode);
                                close();
                              }}
                            >
                              {getDateRangeTitle(mode)}
                            </button>
                          ))}
                        <button
                          className={`rounded border-none p-2 ${
                            dateRangeMode == "custom"
                              ? "bg-gray-300 hover:bg-gray-300"
                              : "bg-gray-100 hover:bg-gray-200"
                          }`}
                        >
                          Custom Range
                        </button>
                      </div>
                      <div className="flex items-center justify-around gap-2.5">
                        <div className="flex flex-col items-center gap-1.5">
                          <h3 className="text-2xl font-normal">Start</h3>
                          <DatePicker
                            selected={startDate}
                            onChange={(date: Date) => {
                              setDateRangeMode("custom");
                              setStartDate(date);
                              if (isSameDayOrAfter(date, endDate)) {
                                setEndDate(date);
                              }
                            }}
                            inline
                          />
                        </div>
                        <div className="flex flex-col items-center gap-1.5">
                          <h3 className="text-2xl font-normal">End</h3>
                          <DatePicker
                            selected={endDate}
                            onChange={(date: Date) => {
                              setDateRangeMode("custom");
                              setEndDate(date);
                              if (isSameDayOrBefore(date, startDate)) {
                                setStartDate(date);
                              }
                            }}
                            inline
                          />
                        </div>
                      </div>
                    </div>
                    <button className="absolute right-6 top-6" onClick={close}>
                      <XMarkIcon className="icon-lg icon-gray" />
                    </button>
                  </DialogComponent>
                </div>
                <select
                  aria-label="Date Range"
                  className="ml-1.5 h-full cursor-pointer border-transparent px-1.5 py-2"
                  value={dateRangeMode}
                  onChange={(e) => {
                    const mode = e.target.value as DateRangeModes;
                    const [start, end] = getAppropriateDateRange(mode, {
                      terms: user.school?.terms || [],
                    });

                    setSourceStartDate(start);
                    setSourceEndDate(end);
                    setDateRangeMode(mode);
                  }}
                >
                  <option value="today">Today</option>
                  <option value="last-week">Last Week</option>
                  <option value="this-week">This Week</option>
                  <option value="next-week">Next Week</option>
                  {findPreviousTerm(new Date(), user.school?.terms || []) ? (
                    <option value="last-term">Last Term</option>
                  ) : null}
                  {findTerm(new Date(), user.school?.terms || []) ? (
                    <option value="this-term">This Term</option>
                  ) : null}
                  {findNextTerm(new Date(), user.school?.terms || []) ? (
                    <option value="next-term">Next Term</option>
                  ) : null}
                  <option value="this-year">This Year</option>
                  <option value="past-present">Past</option>
                  <option value="present-future">Future</option>
                  <option value="all-time">All Time</option>
                  <option value="custom" hidden>
                    Custom
                  </option>
                </select>
              </div>

              {orderSessions?.length ? (
                <input
                  type="text"
                  value={search}
                  placeholder="Search..."
                  onChange={(e) => setSearch(e.target.value)}
                  className={search ? "" : "print:hidden"}
                />
              ) : null}
              {searchedSessions.length ? (
                <>
                  <DeleteManyOrderSessions
                    searchedSessions={searchedSessions}
                    search={search}
                    removeOrderSessions={removeOrderSessions}
                  />
                  <Tooltip
                    content={`${
                      expandedOrders.size == orderSessions.length
                        ? "Hide"
                        : "Show"
                    } All Orders`}
                  >
                    <button
                      onClick={() => {
                        if (expandedOrders.size == orderSessions.length) {
                          // clear the set
                          setExpandedOrders(new Set());
                        } else {
                          // fill the set
                          setExpandedOrders(
                            new Set(orderSessions.map(({ id }) => id))
                          );
                        }
                      }}
                    >
                      <ChevronDoubleDownIcon
                        className={`icon-md icon-gray transform transition-all duration-300 ease-in-out ${
                          expandedOrders.size == orderSessions.length
                            ? "rotate-180"
                            : ""
                        }`}
                      />
                    </button>
                  </Tooltip>
                </>
              ) : null}
            </>
          )}
          {searchedSessions.length ? (
            <Tooltip content="Print Orders">
              <button
                className="print:hidden"
                onClick={() => {
                  mixpanel.track("Clicked print", { page: "summary" });
                  window.print();
                }}
              >
                <PrinterIcon className="icon-md icon-green" />
              </button>
            </Tooltip>
          ) : null}
          {/* <OrdersPusherInfinite
            key={orderSessions ? 1 : 0}
            user={user}
            document={OrderSessionsDocument}
            variables={variables}
            addOrderSessions={addOrderSessions}
            removeOrderSessions={removeOrderSessions}
            removeOrders={removeOrders}
          /> */}
        </div>
      </TitleBar>

      {error ? <ErrorMessage error={error} /> : null}

      {!orderSessions ? (
        fetching ? (
          <motion.div
            key="loading-without-orders"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { delay: 0.25 },
            }}
            exit={{ opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              duration: 0.25,
              // delay: 0.25,
            }}
            className="relative flex flex-col items-center justify-center space-y-5"
          >
            <div className="spinner mb-8 mt-16 h-88 w-88" />
            <h4 className="absolute right-1/2 top-1/2 flex -translate-y-1/2 translate-x-1/2 transform items-center gap-4">
              Loading Orders...
            </h4>
          </motion.div>
        ) : (
          <motion.div
            key="no-results"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { delay: 0.25 },
            }}
            exit={{ opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              duration: 0.25,
              // delay: 0.25,
            }}
            className="flex flex-col items-center justify-center space-y-5"
          >
            <UndrawQuestions className="mb-8 mt-16 h-88" />
            <h3 className="font-medium">How strange? There are no results!</h3>
          </motion.div>
        )
      ) : !classes.length ? (
        fetching ? (
          <motion.div
            key="loading-with-orders"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { delay: 0.25 },
            }}
            exit={{ opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              duration: 0.25,
              // delay: 0.25,
            }}
            className="relative flex flex-col items-center justify-center space-y-5"
          >
            <div className="spinner mb-8 mt-16 h-88 w-88" />
            <h4 className="absolute right-1/2 top-1/2 flex -translate-y-1/2 translate-x-1/2 transform items-center gap-4">
              Loading Orders...
            </h4>
          </motion.div>
        ) : (
          <motion.div
            key="no-classes"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { delay: 0.25 },
            }}
            exit={{ opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              duration: 0.25,
              // delay: 0.25,
            }}
            className="flex flex-col items-center justify-center space-y-5"
          >
            <UndrawTeaching className="mb-8 mt-16 h-88" />
            <h3 className="font-medium">
              There are no classes to order for... yet.
            </h3>
            {isTeacher(user) ? (
              <Link prefetch={false} href="/create-class">
                <button className="btn btn-primary font-normal">
                  <PlusCircleIcon className="w-6 text-green-800" /> Create Your
                  First Class
                </button>
              </Link>
            ) : (
              <Link prefetch={false} href="/join-class">
                <button className="btn btn-primary">Join a Class</button>
              </Link>
            )}
          </motion.div>
        )
      ) : fragmentID ? (
        orderSessions
          .filter((os) => !deleted.includes(os.id))
          .filter(hasFragmentID).length ? (
          <OrdersGrid>
            {orderSessions.filter(hasFragmentID).map((orderSession) => (
              <OrderSession
                key={orderSession.id}
                currentUser={user}
                orderSession={orderSession}
                isSearching={false}
                expandedOrders={new Set([orderSession.id])}
                showDistance={showDistance}
                setShowDistance={setShowDistance}
                fragmentID={fragmentID}
                updateStaticOrderSession={updateStaticOrderSession}
                updateStaticOrder={updateStaticOrder}
                priceWarnings={user.school?.priceWarnings || []}
                deleted={deleted.includes(orderSession.id)}
                setDeleted={setDeleted}
              />
            ))}
          </OrdersGrid>
        ) : (
          <motion.div
            key="not-found"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { delay: 0.25 },
            }}
            exit={{ opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              duration: 0.25,
              // delay: 0.25,
            }}
            className="flex flex-col items-center justify-center space-y-5"
          >
            <UndrawNotFound className="mb-8 mt-16 h-88" />
            <h4>
              No order was found with the given ID. Perhpas it has been deleted?
            </h4>
            {/* TODO: Add a link to the recover orders page. */}
          </motion.div>
        )
      ) : !searchedSessions.length ? (
        <motion.div
          key="no-orders"
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            transition: { delay: 0.25 },
          }}
          exit={{ opacity: 0 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
            duration: 0.25,
            // delay: 0.25,
          }}
          className="flex flex-col items-center justify-center space-y-5"
        >
          {search ? (
            <>
              <UndrawNotFound className="mb-8 mt-16 h-88" />
              <h3 className="font-medium text-gray-700">
                There are no orders matching your search.
              </h3>
            </>
          ) : (
            <OrderDialogContext.Consumer>
              {({ setOrderDialog }) => (
                <>
                  <UndrawEmpty className="mb-8 mt-16 h-88" />
                  <h3 className="font-medium text-gray-700">
                    There are no orders made for{" "}
                    {dateRangeMode === "today"
                      ? "today"
                      : dateRangeMode === "this-week"
                      ? "this week"
                      : dateRangeMode === "last-week"
                      ? "last week"
                      : dateRangeMode === "next-week"
                      ? "next week"
                      : dateRangeMode === "this-term"
                      ? "this term"
                      : dateRangeMode === "last-term"
                      ? "last term"
                      : dateRangeMode === "next-term"
                      ? "next term"
                      : dateRangeMode === "this-year"
                      ? "this year"
                      : dateRangeMode === "all-time"
                      ? "all time"
                      : dateRangeMode === "custom"
                      ? `between ${format(startDate, "MMM do")} and ${format(
                          endDate,
                          "MMM do"
                        )}`
                      : dateRangeMode === "past-present"
                      ? "the past"
                      : dateRangeMode === "present-future"
                      ? "the future"
                      : dateRangeMode === "future"
                      ? "the future"
                      : "the given date range."}
                  </h3>
                  {!(isStudent(user) && !user.school?.permMakeOrders) ? (
                    <button
                      className="btn btn-primary font-normal"
                      onClick={() => setOrderDialog(true)}
                    >
                      <PlusCircleIcon className="w-6 text-green-800" />
                      <NewTeacherContext.Consumer>
                        {(isNewTeacher) =>
                          isNewTeacher
                            ? "Make Your First Order!"
                            : "Make an Order"
                        }
                      </NewTeacherContext.Consumer>
                    </button>
                  ) : null}
                </>
              )}
            </OrderDialogContext.Consumer>
          )}
        </motion.div>
      ) : (
        <OrdersGrid
          className={
            searchedSessions.length >= 10 ? "relative screen:pr-18" : ""
          }
        >
          {insertTemporalIndicators(searchedSessions)
            .sort((a, b) =>
              sort == "ascending"
                ? Number(new Date(a.date)) - Number(new Date(b.date))
                : Number(new Date(b.date)) - Number(new Date(a.date))
            )
            .map((orderSession) => (
              <OrderSession
                key={orderSession.id}
                boundary={
                  searchedSessions.length >= 10 && orderSession.boundary
                }
                currentUser={user}
                orderSession={orderSession}
                isSearching={!!search}
                expandedOrders={expandedOrders}
                setExpandedOrders={setExpandedOrders}
                showDistance={showDistance}
                setShowDistance={setShowDistance}
                updateStaticOrderSession={updateStaticOrderSession}
                updateStaticOrder={updateStaticOrder}
                priceWarnings={user.school?.priceWarnings || []}
                deleted={deleted.includes(orderSession.id)}
                setDeleted={setDeleted}
              />
            ))}
        </OrdersGrid>
      )}

      <InfiniteLoader
        {...{
          limitReached,
          fetching,
          orderSessions: orderSessions || [],
          setCursor,
        }}
      />
    </div>
  );
};

interface InfiniteLoaderProps {
  limitReached: boolean;
  fetching: boolean;
  orderSessions: OrderSessionsQuery["orderSessions"];
  setCursor: Dispatch<SetStateAction<string>>;
}

const InfiniteLoader: React.FC<InfiniteLoaderProps> = ({
  limitReached,
  fetching,
  orderSessions,
  setCursor,
}) => {
  useIntersectionObserver(
    "intersection-target",
    [fetching, orderSessions.length],
    (entry) => {
      // setTitleOpacity(entry.intersectionRatio);
      console.log(entry.intersectionRatio, limitReached, fetching);
      if (limitReached || fetching) return;

      if (entry.intersectionRatio > 0) {
        // console.log("would have fetched more");

        // console.group("fetching more");
        // console.log("limitReached", limitReached);
        // console.log("orderSessions.length", orderSessions.length);
        // console.log(
        //   "derived cursor",
        //   orderSessions[orderSessions.length - 1]?.id
        // );
        // console.groupEnd();
        setCursor(orderSessions[orderSessions.length - 1]?.id);
      }
    }
  );

  useIntersectionObserver(
    "intersection-target-two",
    [fetching, orderSessions.length],
    (entry) => {
      // setTitleOpacity(entry.intersectionRatio);
      console.log(entry.intersectionRatio, limitReached, fetching);
      if (limitReached || fetching) return;

      if (entry.intersectionRatio > 0) {
        // console.log("would have fetched more");

        // console.group("fetching more");
        // console.log("limitReached", limitReached);
        // console.log("orderSessions.length", orderSessions.length);
        // console.log(
        //   "derived cursor",
        //   orderSessions[orderSessions.length - 1]?.id
        // );
        // console.groupEnd();
        setCursor(orderSessions[orderSessions.length - 1]?.id);
      }
    }
  );

  return (
    <div
      className={classNames(
        limitReached ? "hidden" : "mt-24 flex items-center justify-center"
      )}
    >
      <div
        id="intersection-target"
        className="h-5 w-5 translate-y-[-3000px] transform-gpu rounded-full bg-blue-100/0"
      />
      {/* A target closer to the loader to... */}
      {/* ...avoid issues of the viewport being between the spinner and the main target */}
      <div
        id="intersection-target-two"
        className="h-5 w-5 translate-y-[-200px] transform-gpu rounded-full bg-blue-100/0"
      />
      <div
        className={classNames(
          "spinner h-8 w-8",
          fetching && orderSessions.length ? "opacity-100" : "opacity-0"
        )}
      />
    </div>
  );
};

export default Orders;
